// src/utils/fundMath.js
// Single source of truth for sinking fund balances, projections and tile tints.
//
// SinkingFundsTab renders from these; App.js builds the widget payload from the
// same functions, so the widget can never disagree with the tab. The widget
// itself does no arithmetic — it draws what it is given.

import { countsInProjection, signOf } from "./futureIncome"; // plan_25, plan_28

// Eight tints per theme. A fund takes the tint at its position in the list,
// cycling after eight. wash = tile background, ink = figures + saved bar,
// mid = projected bar.
export const FUND_TINTS = {
  pink: [
    { wash: '#FAE3EC', ink: '#C23A72', mid: '#DE8FAF' },
    { wash: '#E4EDF8', ink: '#3C6693', mid: '#8FAAC6' },
    { wash: '#EBF3E1', ink: '#5C7A4A', mid: '#A2BC90' },
    { wash: '#E9E1F5', ink: '#7A5FB0', mid: '#B197D9' },
    { wash: '#F0E2EE', ink: '#7E4576', mid: '#BD91B6' },
    { wash: '#DEEFEB', ink: '#2C6A60', mid: '#85B2AA' },
    { wash: '#FBE4D9', ink: '#A85431', mid: '#D9A085' },
    { wash: '#E3E8F9', ink: '#4A5AA3', mid: '#9BA5D2' }
  ],

  warm: [
    { wash: '#F6E3DC', ink: '#A34F3A', mid: '#D09A87' },
    { wash: '#EDF0DC', ink: '#5F6B2B', mid: '#A8B173' },
    { wash: '#F6EBD2', ink: '#8A6516', mid: '#D3B274' },
    { wash: '#E2EFE4', ink: '#2D5A3D', mid: '#85B294' },
    { wash: '#F7E2CF', ink: '#9B5A22', mid: '#D4A374' },
    { wash: '#E2EBF0', ink: '#3F6480', mid: '#93B0C2' },
    { wash: '#EDE2EA', ink: '#75466B', mid: '#B593AC' },
    { wash: '#DFEDEA', ink: '#2E675F', mid: '#8AB2AB' }
  ],

  // Purple (admin-only): every fund and account in one purple family -
  // dark purple for the saved bar and figures, lighter lavender for projected.
  // Calm (purple_16): soft grey wash, ink text, pale lavender bars.
  purple: Array.from({ length: 8 }, () => ({ wash: '#F7F5F9', ink: '#1E1630', bar: '#C9B8EE', mid: '#E4DCF1' })),

  // w2_09: Worthly 2.0 - the same pastels a fund has in the app (planFundTint order).
  plan: [
    { wash: '#DFE9F9', ink: '#3C6DB5', mid: '#A9C6F0' },
    { wash: '#ECE6FA', ink: '#7A5DD3', mid: '#B9A6F0' },
    { wash: '#FAE4ED', ink: '#C04B7C', mid: '#F2AFC8' },
    { wash: '#DFE9F9', ink: '#3C6DB5', mid: '#A9C6F0' },
    { wash: '#DFF2E7', ink: '#2F8756', mid: '#9ED9B6' },
    { wash: '#FCF3D2', ink: '#8A7A3C', mid: '#F3DE8E' },
    { wash: '#ECE6FA', ink: '#7A5DD3', mid: '#B9A6F0' }
  ],

  blue: [
    { wash: '#E0E5FA', ink: '#3A4DA8', mid: '#93A0DC' },
    { wash: '#DDEDF8', ink: '#26658C', mid: '#85B6D1' },
    { wash: '#DFF1E7', ink: '#237055', mid: '#85C0A6' },
    { wash: '#E9E2F8', ink: '#6B4FB0', mid: '#A994D9' },
    { wash: '#F8E2EC', ink: '#A73C6B', mid: '#D693AF' },
    { wash: '#F7EDD8', ink: '#8A6318', mid: '#D5B071' },
    { wash: '#DCEFF0', ink: '#216B6E', mid: '#83BABC' },
    { wash: '#F9E4DC', ink: '#A6512F', mid: '#D79C86' }
  ]
};

// reorder_01: each fund's colour slot. A fund that has been reordered carries
// its own slot (fund.tint) so its colour moves with it. Funds without one take
// the lowest free slot, which is just their position when nobody has
// reordered, so nothing changes for anyone who never reorders.
export const fundTintSlots = (funds = []) => {
  const taken = new Set();
  (funds || []).forEach((f) => { if (f && Number.isInteger(f.tint)) taken.add(f.tint); });
  let next = 0;
  return (funds || []).map((f) => {
    if (f && Number.isInteger(f.tint)) return f.tint;
    while (taken.has(next)) next += 1;
    taken.add(next);
    return next;
  });
};

export const getFundTint = (index, theme = 'pink') => {
  const set = FUND_TINTS[theme] || FUND_TINTS.pink;
  return set[index % set.length];
};

// Balance = starting balance + deposits + interest - withdrawals, counting only
// transactions up to `cutoff` (the end of the month being viewed; omit for all).
export const getFundBalance = ({ fund, transactions = [], cutoff = null }) => {
  if (!fund) return 0;
  const fundId = fund.id;
  const startingBalance = fund.starting_balance || 0;
  const relevant = cutoff
    ? (transactions || []).filter(t => new Date(t.date) <= cutoff)
    : (transactions || []);

  const deposits = relevant
    .filter(t => t.account_id === fundId && t.account_type === 'fund' && t.transaction_type === 'deposit')
    .reduce((sum, t) => sum + t.amount, 0);

  const withdrawals = relevant
    .filter(t => t.account_id === fundId && t.account_type === 'fund' && t.transaction_type === 'withdrawal')
    .reduce((sum, t) => sum + t.amount, 0);

  const interest = relevant
    .filter(t => t.interest_account_id === fundId && t.interest_account_type === 'fund' && t.is_interest)
    .reduce((sum, t) => sum + t.amount, 0);

  return startingBalance + deposits + interest - withdrawals;
};

// Projected = balance + any planned payments not yet moved into accounts.
export const getProjectedAmount = ({ fund, transactions = [], extraPayments = [], cutoff = null }) => {
  if (!fund) return 0;
  const fundId = fund.id;
  const currentAmount = getFundBalance({ fund, transactions, cutoff });

  const pendingPayments = (extraPayments || [])
    .filter(p => !p.moved_to_account && countsInProjection(p, cutoff ? cutoff.getFullYear() : null))
    .reduce((sum, p) => {
      if (p.fund_allocations && p.fund_allocations[fundId]) {
        return sum + signOf(p) * p.fund_allocations[fundId];
      }
      const fundsList = p.designated_funds || (p.designated_fund ? [p.designated_fund] : []);
      if (fundsList.includes(fundId)) {
        return sum + signOf(p) * ((p.amount || 0) / fundsList.length);
      }
      return sum;
    }, 0);

  return currentAmount + pendingPayments;
};

// The widget payload. `limit` is the subscription cap, so the widget shows
// exactly the funds the Funds tab shows and no locked ones.
export const buildFundsWidgetData = ({
  sinkingFunds = [],
  transactions = [],
  extraPayments = [],
  limit = 3,
  theme = 'pink',
  cutoff = null,
} = {}) => {
  const slots = fundTintSlots(sinkingFunds || []); // reorder_01
  return (sinkingFunds || []).slice(0, limit).map((fund, index) => {
    const saved = getFundBalance({ fund, transactions, cutoff });
    const projected = getProjectedAmount({ fund, transactions, extraPayments, cutoff });
    const goal = fund.goal || 0;
    const tint = getFundTint(slots[index], theme);
    return {
      id: String(fund.id),
      name: fund.name || 'Fund',
      emoji: fund.emoji || '\u{1F4B0}',
      saved,
      goal,
      projected,
      savedPercent: goal > 0 ? Math.min(100, Math.round((saved / goal) * 100)) : 0,
      projectedPercent: goal > 0 ? Math.min(100, Math.round((projected / goal) * 100)) : 0,
      wash: tint.wash,
      ink: tint.ink,
      mid: tint.mid,
    };
  });
};
